import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["video", "overlay"]

  connect() {
    this.isMobile = 'ontouchstart' in window
    this.isHovered = false
    this.hasUserInteracted = false // Track si l'utilisateur a interagi
    this.setupVideo()
    this.setupModal()
    
    // Essayer de démarrer la vidéo
    this.startVideo()
  }

  disconnect() {
    if (this.fadeInterval) {
      clearInterval(this.fadeInterval)
    }
    this.removeModal()
  }

  setupVideo() {
    const video = this.videoTarget
    
    // Configuration initiale
    video.muted = true
    video.loop = true
    video.volume = 0
    video.playsinline = true
    video.autoplay = true
    
    // Filtre noir et blanc par défaut
    video.style.filter = 'grayscale(100%)'
    video.style.transition = 'filter 0.5s ease, transform 0.3s ease'
    
    // Transition pour le container
    this.element.style.transition = 'transform 0.3s ease'
  }

  startVideo() {
    const video = this.videoTarget
    
    // Essayer de démarrer la vidéo en muet (plus de chance de réussir)
    video.muted = true
    const playPromise = video.play()
    
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          // La vidéo a démarré avec succès
        })
        .catch(error => {
          // Autoplay bloqué - on attend une interaction
          
          // Au premier hover ou clic, on démarre tout
          const startOnInteraction = () => {
            if (!this.hasUserInteracted) {
              this.hasUserInteracted = true
              // Démarrer toutes les vidéos de la page
              document.querySelectorAll('[data-controller="video-hover"] video').forEach(v => {
                v.muted = true
                v.play().catch(() => {})
              })
            }
          }
          
          // Écouter les interactions
          this.element.addEventListener('mouseenter', startOnInteraction, { once: true })
          this.element.addEventListener('click', startOnInteraction, { once: true })
          this.element.addEventListener('touchstart', startOnInteraction, { once: true })
        })
    }
  }

  // HOVER ENTER
  mouseenter(event) {
    if (this.isMobile) return
    
    this.isHovered = true
    const video = this.videoTarget
    
    // Si c'est la première interaction, démarrer toutes les vidéos
    if (!this.hasUserInteracted) {
      this.hasUserInteracted = true
      // Démarrer toutes les vidéos sur la page
      document.querySelectorAll('[data-controller="video-hover"] video').forEach(v => {
        v.muted = true
        v.play().catch(() => {})
      })
    }
    
    // S'assurer que CETTE vidéo joue
    if (video.paused) {
      video.muted = true // Commencer en muet pour garantir le play
      video.play()
        .then(() => {
          // Une fois que ça joue, on peut activer le son
          setTimeout(() => {
            if (this.isHovered) { // Vérifier qu'on est toujours en hover
              this.activateAudio()
            }
          }, 100)
        })
        .catch(() => {})
    } else {
      // La vidéo joue déjà, on active juste le son
      this.activateAudio()
    }
    
    // Transition visuelle
    video.style.filter = 'grayscale(0%)'
    video.style.transform = 'scale(1.05)'
    
    // Élévation du container
    this.element.style.transform = 'scale(1.02)'
    this.element.style.zIndex = '10'
    
    // Hide overlay
    if (this.hasOverlayTarget) {
      this.overlayTarget.style.opacity = '0'
    }
  }

  // HOVER LEAVE
  mouseleave(event) {
    if (this.isMobile) return
    
    this.isHovered = false
    const video = this.videoTarget
    
    // NE JAMAIS stopper la vidéo
    
    // Reset visuel
    video.style.filter = 'grayscale(100%)'
    video.style.transform = 'scale(1)'
    
    // Reset container
    this.element.style.transform = 'scale(1)'
    this.element.style.zIndex = '1'
    
    // Désactiver le son avec fade out
    this.deactivateAudio()
    
    // Show overlay
    if (this.hasOverlayTarget) {
      this.overlayTarget.style.opacity = '0.4'
    }
  }

  activateAudio() {
    const video = this.videoTarget
    
    // Clear any existing fade
    if (this.fadeInterval) {
      clearInterval(this.fadeInterval)
    }
    
    // S'assurer que la vidéo joue avant d'activer le son
    if (video.paused) return
    
    // Unmute et commencer le fade in
    video.muted = false
    video.volume = 0
    
    let currentVolume = 0
    const targetVolume = 0.3
    const increment = 0.03
    
    this.fadeInterval = setInterval(() => {
      if (!this.isHovered) {
        clearInterval(this.fadeInterval)
        return
      }
      
      currentVolume += increment
      if (currentVolume >= targetVolume) {
        currentVolume = targetVolume
        clearInterval(this.fadeInterval)
        this.fadeInterval = null
      }
      video.volume = currentVolume
    }, 30)
  }

  deactivateAudio() {
    const video = this.videoTarget
    
    // Clear any existing fade
    if (this.fadeInterval) {
      clearInterval(this.fadeInterval)
    }
    
    let currentVolume = video.volume || 0
    const decrement = 0.03
    
    this.fadeInterval = setInterval(() => {
      currentVolume -= decrement
      if (currentVolume <= 0) {
        currentVolume = 0
        video.muted = true
        clearInterval(this.fadeInterval)
        this.fadeInterval = null
      }
      video.volume = Math.max(0, currentVolume)
    }, 30)
  }

  // CLICK pour modal fullscreen
  click(event) {
    event.preventDefault()
    event.stopPropagation()
    
    // S'assurer que toutes les vidéos sont démarrées
    if (!this.hasUserInteracted) {
      this.hasUserInteracted = true
      document.querySelectorAll('[data-controller="video-hover"] video').forEach(v => {
        v.muted = true
        v.play().catch(() => {})
      })
    }
    
    this.openModal()
  }

  // TAP pour mobile
  tap(event) {
    if (!this.isMobile) return
    
    event.preventDefault()
    event.stopPropagation()
    
    // S'assurer que toutes les vidéos sont démarrées
    if (!this.hasUserInteracted) {
      this.hasUserInteracted = true
      document.querySelectorAll('[data-controller="video-hover"] video').forEach(v => {
        v.muted = true
        v.play().catch(() => {})
      })
    }
    
    // Sur mobile, le tap toggle l'état
    if (this.isHovered) {
      this.mouseleave()
    } else {
      this.mouseenter()
    }
  }

  // MODAL SETUP
  setupModal() {
    this.modal = document.createElement('div')
    this.modal.className = 'fixed inset-0 z-50 hidden bg-black/90'
    this.modal.innerHTML = `
      <div class="relative w-full h-full flex items-center justify-center p-4 lg:p-8">
        <button class="absolute top-4 right-4 lg:top-8 lg:right-8 text-white/60 hover:text-white text-5xl font-light transition-colors z-10">
          ×
        </button>
        <div class="relative w-full h-full max-w-[90vh] max-h-[90vh] flex items-center justify-center">
          <video class="w-full h-full object-contain" controls autoplay loop></video>
        </div>
      </div>
    `
    
    document.body.appendChild(this.modal)
    
    // Event listeners
    this.modal.querySelector('button').addEventListener('click', () => this.closeModal())
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal || e.target.classList.contains('relative')) {
        this.closeModal()
      }
    })
    
    // ESC pour fermer
    this.handleEsc = (e) => {
      if (e.key === 'Escape') this.closeModal()
    }
  }

  openModal() {
    if (!this.modal) return
    
    const modalVideo = this.modal.querySelector('video')
    const currentVideoSrc = this.videoTarget.querySelector('source').src
    modalVideo.src = currentVideoSrc
    modalVideo.volume = 0.5
    
    // Afficher la modal
    this.modal.classList.remove('hidden')
    requestAnimationFrame(() => {
      this.modal.style.opacity = '0'
      this.modal.style.transition = 'opacity 0.2s ease'
      requestAnimationFrame(() => {
        this.modal.style.opacity = '1'
      })
    })
    
    // Pause la vidéo originale
    this.videoTarget.pause()
    
    // Event listeners
    document.addEventListener('keydown', this.handleEsc)
    document.body.style.overflow = 'hidden'
  }

  closeModal() {
    if (!this.modal) return
    
    const modalVideo = this.modal.querySelector('video')
    if (modalVideo) {
      modalVideo.pause()
      modalVideo.currentTime = 0
      modalVideo.src = ''
    }
    
    // Animation de fermeture
    this.modal.style.opacity = '0'
    setTimeout(() => {
      this.modal.classList.add('hidden')
      
      // Reprendre la vidéo originale
      this.videoTarget.play().catch(() => {})
      
      // Cleanup
      document.removeEventListener('keydown', this.handleEsc)
      document.body.style.overflow = ''
    }, 200)
  }

  removeModal() {
    if (this.modal && this.modal.parentNode) {
      this.modal.parentNode.removeChild(this.modal)
    }
  }
}